import { storeId } from '@/stores/storeId'
import { defineStore } from 'pinia'

import { extractFields } from '@/lib/contentHelpers'
import { ContentType } from '@/lib/contentKeys'
import Sunny16ContentService from '@/services/Sunny16ContentService'

import type {
  BatchRequestItem,
  CatalogContentEnvelope,
  ContentSnapshot,
  DeepResolved
} from '@/types/content'

import type {
  BookingNav,
  BookingPageMetaTitles,
  BookingProcessingLabels,
  BookingSharedMessages
} from '@VelocitySquared/sunny16-content-dataobjects-cady/booking'

import { genericMessagesFallback } from '@/content/shared/genericMessages'
import { navFallback } from '@/content/shared/nav'
import { pageMetaTitlesFallback } from '@/content/shared/pageMetaTitles'
import { processingLabelsFallback } from '@/content/shared/processingLabels'

const supportedLocales = (import.meta.env.VITE_SUPPORTED_LOCALES ?? 'en')
  .split(',')
  .map((l: string) => l.trim())
  .filter(Boolean)

const browserLocale = navigator.language.split('-')[0]
const defaultLocale: string = supportedLocales.includes(browserLocale)
  ? browserLocale
  : (import.meta.env.VITE_DEFAULT_LOCALE ?? 'en')

interface ContentStoreState {
  currentLocale: string
  entries: Record<string, ContentSnapshot | null>
  loading: Record<string, boolean>
  batchLoading: boolean
  lastError: string | null
  catalogContent: CatalogContentEnvelope | null
  catalogContentLoading: boolean
}

/**
 * Walks an object and unwraps ContentRef-like values, merging the local
 * fallback at every level so unauthored or partially-authored CMS entries
 * fall through to the literals.
 *
 * - Singular ContentRef objects ({ _ref, fields: T }) are unwrapped to T
 *   and recursed against the parent fallback's matching child slot. This
 *   keeps newly-added child-class fields populated from the local fallback
 *   until Marketing authors them.
 * - Non-ref plain objects (image assets, nested groups) merge per-field
 *   against the matching fallback child.
 * - Arrays of ContentRef objects have their fields extracted and sorted
 *   by sortOrder; if every ref is `_missing` the fallback array is used.
 * - At every level, CMS-set fields (non-null, non-empty-string) win;
 *   null / undefined / "" CMS values fall through to fallback.
 */
function resolveDeep<T>(obj: T, fallback?: T): T {
  if (obj == null || typeof obj !== 'object') return obj
  const fb = fallback as Record<string, unknown> | undefined
  // Start from the fallback shape so any keys the CMS payload omits
  // entirely still appear, then overlay populated CMS values.
  const result: Record<string, unknown> = fb ? { ...fb } : {}
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const ref = value as Record<string, unknown>
      if ('_ref' in ref && 'fields' in ref && ref.fields) {
        result[key] = resolveDeep(ref.fields, fb?.[key])
        continue
      }
      result[key] = resolveDeep(ref, fb?.[key])
      continue
    }
    if (Array.isArray(value) && value.length > 0) {
      const first = value[0]
      if (first && typeof first === 'object') {
        const isRefArray = '_ref' in first || 'fields' in first
        if (isRefArray || 'sortOrder' in first) {
          const unwrapped = value
            .filter(
              (item: unknown) =>
                !(
                  item &&
                  typeof item === 'object' &&
                  '_missing' in item &&
                  (item as Record<string, unknown>)._missing
                )
            )
            .map((item: unknown) => {
              if (item && typeof item === 'object' && 'fields' in item) {
                return resolveDeep((item as { fields: unknown }).fields)
              }
              return item
            })
          if (unwrapped.length === 0 && fb && Array.isArray(fb[key])) {
            result[key] = fb[key]
            continue
          }
          const firstUnwrapped = unwrapped[0]
          if (
            firstUnwrapped &&
            typeof firstUnwrapped === 'object' &&
            'sortOrder' in firstUnwrapped
          ) {
            unwrapped.sort(
              (a: unknown, b: unknown) =>
                ((a as Record<string, number>).sortOrder ?? 0) -
                ((b as Record<string, number>).sortOrder ?? 0)
            )
          }
          result[key] = unwrapped
          continue
        }
      }
      result[key] = value
      continue
    }
    // Primitive (or empty array). Populated CMS value wins; otherwise the
    // fallback already in `result` stands.
    if (value != null && value !== '') {
      result[key] = value
    }
  }
  return result as T
}

/** Builds a cache key from content type and identifier. */
function cacheKey(contentType: string, identifier: string): string {
  return `${contentType}::${identifier}`
}

export const useContentStore = defineStore(storeId.CONTENT, {
  state: (): ContentStoreState => ({
    currentLocale: defaultLocale,
    entries: {},
    loading: {},
    batchLoading: false,
    lastError: null,
    catalogContent: null,
    catalogContentLoading: false
  }),
  getters: {
    supportedLocales(): string[] {
      return supportedLocales
    },
    isMultiLocale(): boolean {
      return supportedLocales.length > 1
    },
    /** Returns the cached snapshot for a content type + identifier, or null. */
    getEntry(): (contentType: string, identifier: string) => ContentSnapshot | null {
      return (contentType, identifier) => this.entries[cacheKey(contentType, identifier)] ?? null
    },
    /** Returns the extracted fields for a content type + identifier, or null. */
    getFields(): <T = Record<string, unknown>>(
      contentType: string,
      identifier: string
    ) => T | null {
      return <T = Record<string, unknown>>(contentType: string, identifier: string) => {
        const snapshot = this.entries[cacheKey(contentType, identifier)]
        return extractFields<T>(snapshot as ContentSnapshot<T> | null) ?? null
      }
    },
    /** Returns whether a specific content entry is currently loading. */
    isLoading(): (contentType: string, identifier: string) => boolean {
      return (contentType, identifier) => this.loading[cacheKey(contentType, identifier)] ?? false
    },
    /** Returns catalog content grouped by content type. */
    getCatalogContentByType(): (contentType: string) => Record<string, ContentSnapshot | null> {
      return (contentType) => this.catalogContent?.content[contentType] ?? {}
    },
    /**
     * Returns CMS fields if available, otherwise the provided fallback.
     * All `ContentRef` wrappers are recursively unwrapped — both singular
     * refs and ref arrays. Plain fallback objects pass through as-is.
     * Arrays with `sortOrder` are sorted automatically.
     *
     * Pass the raw CMS type as `T` — the return type is `DeepResolved<T>`,
     * stripping all ContentRef wrappers so consumers use plain CMS types.
     */
    resolve(): <T>(contentType: string, identifier: string, fallback: T) => DeepResolved<T> {
      return <T>(contentType: string, identifier: string, fallback: T) => {
        const raw = this.getFields<T>(contentType, identifier)
        // `resolveDeep` handles the fallback merge at every level — from
        // the top-level payload down through ContentRef-unwrapped children
        // — so unauthored CMS fields fall through to the local fallback.
        return resolveDeep(raw ?? fallback, fallback) as DeepResolved<T>
      }
    },
    /**
     * Extracts, sorts, and returns typed fields from all cached entries
     * of a given content type. Use for batch-fetched standalone entries
     * — use for content types fetched via `fetchBatch` as individual entries
     * (FAQ items, progress steps, gallery images).
     */
    resolveEntries(): <T>(contentType: string) => T[] {
      return <T>(contentType: string) => {
        const entries = this.getEntriesByType(contentType)
        const items: T[] = []
        for (const snapshot of Object.values(entries)) {
          const fields = extractFields<T>(snapshot as ContentSnapshot<T> | null)
          if (fields) items.push(fields)
        }
        const first = items[0] as Record<string, unknown> | undefined
        if (first && typeof first.sortOrder === 'number') {
          items.sort(
            (a, b) =>
              ((a as Record<string, unknown>).sortOrder as number) -
              ((b as Record<string, unknown>).sortOrder as number)
          )
        }
        return items
      }
    },
    /**
     * Shared booking messages (generic toast fallbacks, etc.) resolved against
     * the local fallback. Fetched once at app-init — consumers read directly
     * without needing to import the fallback or wire a per-component computed.
     */
    sharedMessages(): BookingSharedMessages {
      return this.resolve<BookingSharedMessages>(
        ContentType.SHARED_MESSAGES,
        'default',
        genericMessagesFallback
      )
    },
    /**
     * Processing-overlay labels (`setAppProcessing(true, '<label>')`) resolved
     * against the local fallback. Fetched once at app-init — consumers read
     * `contentStore.processingLabels.<field>` directly.
     */
    processingLabels(): BookingProcessingLabels {
      return this.resolve<BookingProcessingLabels>(
        ContentType.PROCESSING_LABELS,
        'default',
        processingLabelsFallback
      )
    },
    /** Shared nav CTA labels resolved against the local fallback. */
    nav(): BookingNav {
      return this.resolve<BookingNav>(ContentType.NAV, 'default', navFallback)
    },
    /** Browser tab titles for every booking-flow route. Consumed by the router `beforeEach` guard. */
    pageMetaTitles(): BookingPageMetaTitles {
      return this.resolve<BookingPageMetaTitles>(
        ContentType.PAGE_META_TITLES,
        'default',
        pageMetaTitlesFallback
      )
    },
    /** Returns all cached entries whose key starts with the given content type prefix. */
    getEntriesByType(): (contentType: string) => Record<string, ContentSnapshot | null> {
      return (contentType) => {
        const prefix = contentType + '::'
        const result: Record<string, ContentSnapshot | null> = {}
        for (const [key, snapshot] of Object.entries(this.entries)) {
          if (key.startsWith(prefix)) {
            result[key.slice(prefix.length)] = snapshot
          }
        }
        return result
      }
    }
  },
  actions: {
    setLocale(locale: string) {
      const base = locale.split('-')[0]
      const resolved = supportedLocales.includes(locale)
        ? locale
        : supportedLocales.includes(base)
          ? base
          : null
      if (!resolved) return
      this.currentLocale = resolved
    },
    /**
     * Fetch multiple content entries in a single batch request.
     * Deduplicates items already in the cache unless `force` is true.
     */
    async fetchBatch(
      items: BatchRequestItem[],
      locale?: string,
      force?: boolean
    ): Promise<{ success: boolean; message: string }> {
      const toFetch = force
        ? items
        : items.filter((item) => !(cacheKey(item.contentType, item.identifier) in this.entries))
      if (toFetch.length === 0) return { success: true, message: '' }
      this.batchLoading = true
      this.lastError = null
      for (const item of toFetch) {
        this.loading[cacheKey(item.contentType, item.identifier)] = true
      }
      const service = new Sunny16ContentService()
      try {
        const response = await service.getBatchContent({
          items: toFetch,
          locale: locale ?? this.currentLocale
        })
        for (const [key, snapshot] of Object.entries(response.results)) {
          const slashIndex = key.indexOf('/')
          if (slashIndex > 0) {
            this.entries[cacheKey(key.slice(0, slashIndex), key.slice(slashIndex + 1))] = snapshot
          } else {
            this.entries[key] = snapshot
          }
        }
        return { success: true, message: '' }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to fetch content'
        console.error('[contentStore] fetchBatch failed', error)
        this.lastError = message
        return { success: false, message }
      } finally {
        for (const item of toFetch) {
          this.loading[cacheKey(item.contentType, item.identifier)] = false
        }
        this.batchLoading = false
      }
    },
    /**
     * Convenience wrapper to fetch a single content entry.
     */
    async fetchContent(
      contentType: string,
      identifier: string,
      locale?: string
    ): Promise<{ success: boolean; message: string }> {
      return this.fetchBatch([{ contentType, identifier }], locale)
    },
    /**
     * Load all content for a booking catalog. Stores the envelope and
     * also populates individual entries for downstream access.
     */
    async loadCatalogContent(catalogId: string): Promise<{ success: boolean; message: string }> {
      this.catalogContentLoading = true
      this.lastError = null
      const service = new Sunny16ContentService()
      try {
        this.catalogContent = await service.getBookingCatalogContent(catalogId)
        for (const [contentType, identifiers] of Object.entries(this.catalogContent.content)) {
          for (const [identifier, snapshot] of Object.entries(identifiers)) {
            this.entries[cacheKey(contentType, identifier)] = snapshot
          }
        }
        return { success: true, message: '' }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load catalog content'
        console.error('[contentStore] loadCatalogContent failed', error)
        this.lastError = message
        return { success: false, message }
      } finally {
        this.catalogContentLoading = false
      }
    },
    /** Clear all state. */
    resetStore() {
      this.entries = {}
      this.loading = {}
      this.batchLoading = false
      this.lastError = null
      this.catalogContent = null
      this.catalogContentLoading = false
    }
  },
  persist: true
})
